import { useState } from "react";
import {
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { IconPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { Can } from "@/components/Can";
import { api } from "@/lib/api";
import { qk } from "@/lib/apiHelpers";
import type {
  CompaniesListResponse,
  OrderListItem,
  OrdersListResponse,
  OrderStatus,
} from "@/types/api";

function formatMoney(n: number): string {
  return n.toLocaleString("ru-RU") + " ₽";
}

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString("ru-RU");
}

function StatusPill({ color, name, locked }: { color?: string; name?: string; locked: boolean }) {
  const bg = color || "#868e96";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        backgroundColor: bg,
        color: "#fff",
        borderRadius: 999,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {name || "—"}
      {locked && <span style={{ fontSize: 11 }}>🔒</span>}
    </span>
  );
}

export default function OrdersPage() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [companyFilter, setCompanyFilter] = useState<string | null>(null);
  const [payStateFilter, setPayStateFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const [debounced] = useDebouncedValue(search, 300);

  // Load order statuses
  const { data: statusesData } = useQuery({
    queryKey: qk.orderStatuses(),
    queryFn: async () => (await api.get<OrderStatus[]>("/api/v1/order-statuses")).data,
    staleTime: 5 * 60 * 1000,
  });

  // Load companies for filter
  const { data: companiesData } = useQuery({
    queryKey: qk.companies({ page: 1, page_size: 100 }),
    queryFn: async () =>
      (await api.get<CompaniesListResponse>("/api/v1/companies?page=1&page_size=100")).data,
    staleTime: 5 * 60 * 1000,
  });

  const filters = {
    page,
    page_size: 50,
    q: debounced,
    status: statusFilter ?? "",
    company_id: companyFilter ?? "",
    pay_state: payStateFilter ?? "",
    date_from: dateRange[0] ? dateRange[0].toISOString().slice(0, 10) : "",
    date_to: dateRange[1] ? dateRange[1].toISOString().slice(0, 10) : "",
  };

  const { data, isLoading } = useQuery({
    queryKey: qk.orders(filters as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("page_size", String(filters.page_size));
      if (filters.q) params.set("q", filters.q);
      if (filters.status) params.set("status", filters.status);
      if (filters.company_id) params.set("company_id", filters.company_id);
      if (filters.pay_state) params.set("pay_state", filters.pay_state);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      return (await api.get<OrdersListResponse>(`/api/v1/orders?${params.toString()}`)).data;
    },
  });

  const statusOptions = [
    { value: "", label: "Все статусы" },
    ...(statusesData ?? []).map((s) => ({ value: s.code, label: s.name })),
  ];

  const companyOptions = [
    { value: "", label: "Все фирмы" },
    ...(companiesData?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  // Финансовые фильтры из Trans-Manager (п.5 ТЗ, «долг заказчиков» и т.п.).
  const payStateOptions = [
    { value: "", label: "Оплата: все" },
    { value: "client_debt", label: "Долг заказчиков" },
    { value: "client_paid", label: "Оплачены заказчиками" },
    { value: "carrier_debt", label: "Не оплачены перевозчикам" },
    { value: "carrier_paid", label: "Оплачены перевозчикам" },
    { value: "any_debt", label: "Любые долги" },
  ];

  const summary = data?.summary;

  const columns: DataTableColumn<OrderListItem>[] = [
    {
      key: "number_internal",
      header: "№",
      width: 70,
      render: (o) => (
        <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{o.number_internal}</span>
      ),
    },
    {
      key: "document_date",
      header: "Дата",
      width: 100,
      render: (o) => formatDate(o.document_date),
    },
    {
      key: "client_name",
      header: "Заказчик",
      render: (o) => o.client_name ?? "—",
      filter: {
        type: "text",
        getValue: (o) => o.client_name ?? "",
        placeholder: "Поиск по заказчику",
      },
    },
    {
      key: "route",
      header: "Маршрут",
      render: (o) => o.route ?? "—",
      filter: {
        type: "text",
        getValue: (o) => o.route ?? "",
        placeholder: "Поиск по маршруту",
      },
    },
    {
      key: "carrier_name",
      header: "Перевозчик",
      render: (o) => o.carrier_name ?? "—",
      filter: {
        type: "text",
        getValue: (o) => o.carrier_name ?? "",
        placeholder: "Поиск по перевозчику",
      },
    },
    {
      key: "driver",
      header: "Водитель",
      render: (o) => o.driver_name || o.driver_text || "—",
      filter: {
        type: "text",
        getValue: (o) => o.driver_name || o.driver_text || "",
        placeholder: "Поиск по водителю",
      },
    },
    {
      key: "manager",
      header: "Менеджер",
      render: (o) => o.responsible_manager_name ?? "—",
      filter: {
        type: "text",
        getValue: (o) => o.responsible_manager_name ?? "",
        placeholder: "Поиск по менеджеру",
      },
    },
    {
      key: "company",
      header: "Своя фирма",
      width: 140,
      render: (o) => o.company_name ?? "—",
      filter: {
        type: "text",
        getValue: (o) => o.company_name ?? "",
        placeholder: "Поиск по фирме",
      },
    },
    {
      key: "creator",
      header: "Логист",
      width: 130,
      render: (o) => o.created_by_name ?? "—",
      filter: {
        type: "text",
        getValue: (o) => o.created_by_name ?? "",
        placeholder: "Поиск по логисту",
      },
    },
    {
      key: "client_amount",
      header: (
        <span style={{ display: "block", textAlign: "right" }}>Заказчик ₽</span>
      ),
      width: 130,
      render: (o) => (
        <span style={{ display: "block", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {o.client_amount.toLocaleString("ru-RU")}
        </span>
      ),
    },
    {
      key: "carrier_amount",
      header: (
        <span style={{ display: "block", textAlign: "right" }}>Перевозчик ₽</span>
      ),
      width: 130,
      render: (o) => (
        <span style={{ display: "block", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {o.carrier_amount.toLocaleString("ru-RU")}
        </span>
      ),
    },
    {
      // Компактная колонка «Оплата заказчик/перевозчик» —
      // по запросу заказчика (видео 29:00). Формат:
      //   ✓/✓  = обе стороны рассчитались
      //   0/✓  = мы заплатили перевозчику, заказчик не оплатил
      //   ..и т.п. Показываем только статус, суммы — в карточке.
      key: "paid",
      header: (
        <span title="Оплата: Заказчик / Перевозчик">Опл.</span>
      ),
      width: 70,
      render: (o) => {
        const cPaid = o.client_paid ?? 0;
        const cTotal = o.client_amount;
        const kPaid = o.carrier_paid ?? 0;
        const kTotal = o.carrier_amount;
        const mark = (paid: number, total: number): string => {
          if (total <= 0) return "—";
          if (paid <= 0) return "0";
          if (paid >= total) return "✓";
          return "½"; // частично
        };
        const cMark = mark(cPaid, cTotal);
        const kMark = mark(kPaid, kTotal);
        const colorFor = (m: string) => m === "✓" ? "green" : m === "0" ? "red" : m === "½" ? "orange" : "gray";
        return (
          <span
            style={{ display: "block", textAlign: "center", fontFamily: "monospace" }}
            title={`Заказчик: ${cPaid.toLocaleString("ru-RU")}/${cTotal.toLocaleString("ru-RU")}\nПеревозчик: ${kPaid.toLocaleString("ru-RU")}/${kTotal.toLocaleString("ru-RU")}`}
          >
            <span style={{ color: `var(--mantine-color-${colorFor(cMark)}-6)` }}>{cMark}</span>
            {" / "}
            <span style={{ color: `var(--mantine-color-${colorFor(kMark)}-6)` }}>{kMark}</span>
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Статус",
      width: 150,
      render: (o) => (
        <StatusPill color={o.status_color} name={o.status_name} locked={o.locked} />
      ),
      filter: {
        type: "text",
        getValue: (o) => o.status_name ?? "",
        placeholder: "Поиск по статусу",
      },
    },
  ];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Журнал заявок</Title>
        <Can perm="orders:write">
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate("/orders/new")}
          >
            + Новая заявка
          </Button>
        </Can>
      </Group>

      <Group gap="sm" wrap="wrap">
        <TextInput
          placeholder="Поиск по №, заказчику, перевозчику, водителю..."
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          style={{ minWidth: 300, flex: 1 }}
        />
        <Select
          placeholder="Все статусы"
          data={statusOptions}
          value={statusFilter ?? ""}
          onChange={(v) => {
            setStatusFilter(v || null);
            setPage(1);
          }}
          style={{ width: 180 }}
          clearable
        />
        <Select
          placeholder="Все фирмы"
          data={companyOptions}
          value={companyFilter ?? ""}
          onChange={(v) => {
            setCompanyFilter(v || null);
            setPage(1);
          }}
          style={{ width: 180 }}
          clearable
        />
        <Select
          placeholder="Оплата: все"
          data={payStateOptions}
          value={payStateFilter ?? ""}
          onChange={(v) => {
            setPayStateFilter(v || null);
            setPage(1);
          }}
          style={{ width: 220 }}
          clearable
        />
        <DatePickerInput
          type="range"
          placeholder="Период (от — до)"
          value={dateRange}
          onChange={(v) => {
            setDateRange(v);
            setPage(1);
          }}
          clearable
          style={{ width: 220 }}
          valueFormat="DD.MM.YYYY"
        />
      </Group>

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        rowKey={(o) => o.id}
        onRowClick={(o) => navigate(`/orders/${o.id}`)}
        emptyMessage="Заявок не найдено"
      />

      {summary && (
        <Paper withBorder p="md" radius="md">
          <Group gap="xl" wrap="wrap">
            <Text size="sm">
              Заказчики:{" "}
              <strong style={{ fontFamily: "monospace" }}>
                {formatMoney(summary.client_total)}
              </strong>
            </Text>
            <Text size="sm">
              Перевозчики:{" "}
              <strong style={{ fontFamily: "monospace" }}>
                {formatMoney(summary.carrier_total)}
              </strong>
            </Text>
            <Text size="sm">
              Комиссия:{" "}
              <strong style={{ fontFamily: "monospace" }}>
                {formatMoney(summary.commission_total)}
              </strong>
            </Text>
            <Text size="sm">
              Заявок:{" "}
              <strong style={{ fontFamily: "monospace" }}>{summary.count}</strong>
            </Text>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
